import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Copy, Check, Download, Laptop, Smartphone, Globe2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { REGISTER } from "@/constants/testIds";

function CopyField({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success("Скопировано");
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 bg-white rounded-lg border border-blue-100 px-3.5 py-2.5">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</div>
        <div className="text-sm font-semibold text-slate-900 truncate" data-testid={testId}>{value}</div>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-[#2563EB] hover:bg-blue-50 transition-colors"
        data-testid={`${testId}-copy-btn`}
      >
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", inn: "", kpp: "", name: "", email: "", password: "" });
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [latestRelease, setLatestRelease] = useState(null);

  useEffect(() => {
    api
      .get("/releases/latest")
      .then(({ data }) => setLatestRelease(data))
      .catch(() => setLatestRelease(null));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== passwordConfirm) {
      setError("Пароли не совпадают");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Компания успешно зарегистрирована в КриптоДок");
      setWelcomeOpen(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="register-page">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-900 font-display">КриптоДок</span>
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-1">Регистрация компании</h2>
        <p className="text-sm text-slate-500 mb-6">Создайте личный кабинет организации для обмена документами</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Название компании</Label>
              <Input required value={form.company_name} onChange={(e) => update("company_name", e.target.value)} data-testid={REGISTER.companyNameInput} placeholder='ООО «Компания»' />
            </div>
            <div className="space-y-1.5">
              <Label>ИНН</Label>
              <Input required value={form.inn} onChange={(e) => update("inn", e.target.value)} data-testid={REGISTER.innInput} placeholder="7701234567" />
            </div>
            <div className="space-y-1.5">
              <Label>КПП (необязательно)</Label>
              <Input value={form.kpp} onChange={(e) => update("kpp", e.target.value)} data-testid={REGISTER.kppInput} placeholder="770101001" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Ваше имя</Label>
            <Input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid={REGISTER.nameInput} placeholder="Иван Иванов" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid={REGISTER.emailInput} placeholder="you@company.ru" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Пароль</Label>
              <Input type="password" required value={form.password} onChange={(e) => update("password", e.target.value)} data-testid={REGISTER.passwordInput} placeholder="••••••••" />
            </div>
            <div className="space-y-1.5">
              <Label>Повторите пароль</Label>
              <Input type="password" required value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} data-testid={REGISTER.passwordConfirmInput} placeholder="••••••••" />
            </div>
          </div>

          {error && <p className="text-sm text-rose-600" data-testid="register-error">{error}</p>}

          <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid={REGISTER.submitButton}>
            {loading ? "Регистрируем…" : "Зарегистрировать и войти"}
          </Button>
        </form>

        <p className="text-sm text-slate-500 mt-6 text-center">
          Уже есть кабинет?{" "}
          <Link to="/login" className="text-blue-600 font-medium hover:underline" data-testid={REGISTER.loginLink}>
            Войти
          </Link>
        </p>
      </div>

      <Dialog open={welcomeOpen} onOpenChange={(v) => !v && navigate("/")}>
        <DialogContent className="sm:max-w-lg" data-testid="register-welcome-modal">
          <DialogHeader>
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#4E9BEE] to-[#2563EB] flex items-center justify-center mb-2">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-xl">Добро пожаловать в КриптоДок!</DialogTitle>
            <DialogDescription>Сохраните эти данные — они понадобятся для входа.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2.5 bg-blue-50/60 rounded-xl p-3.5 border border-blue-100" data-testid="register-welcome-credentials">
            <CopyField label="Email" value={form.email} testId="register-welcome-email" />
            <CopyField label="Пароль" value={form.password} testId="register-welcome-password" />
          </div>

          <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
            <p>
              Чтобы работа с документами была максимально надёжной и гибкой, КриптоДок не привязан
              к браузеру — вместо этого используется полноценное программное обеспечение, установленное
              на вашем устройстве.
            </p>
            <p>
              Установите КриптоДок на компьютер и войдите с указанными выше данными — а дальше сможете
              самостоятельно выдать доступ и другим своим устройствам: второму компьютеру, телефону
              на iOS или Android. Так вы сможете работать там, где удобно, и не зависеть от одного рабочего места.
            </p>
          </div>

          <div className="flex items-center gap-4 text-xs text-slate-400 py-1">
            <span className="flex items-center gap-1.5"><Laptop className="w-3.5 h-3.5" /> Компьютер</span>
            <span className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> iOS / Android</span>
            <span className="flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" /> Везде, где удобно</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
            <a
              href={latestRelease ? `${API}/releases/latest/download` : "#"}
              data-testid="register-welcome-install-btn"
              className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-white px-5 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 active:scale-[0.98] transition-all shadow-md shadow-green-200"
            >
              <Download className="w-4 h-4" /> Установить КриптоДок
            </a>
            <button
              type="button"
              onClick={() => {
                localStorage.setItem("cryptodoc_skip_install", "1");
                navigate("/");
              }}
              data-testid="register-welcome-continue-btn"
              className="flex-1 text-sm font-medium text-slate-500 hover:text-slate-700 px-5 py-2.5 rounded-lg border border-slate-200 transition-colors"
            >
              Продолжить в браузере
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
